import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crosshair, ChevronRight, Search, X, MapPin, Wrench, Users, DollarSign, Activity, Trophy } from "lucide-react";
import { CloudFolderCell } from "@/components/CloudFolders";
import AmandaIntake from "@/components/AmandaIntake";

const STATUS_STYLE = {
  "Target": { color: "#94A3B8", bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.4)" },
  "Contacted": { color: "#60A5FA", bg: "rgba(96,165,250,0.14)", border: "rgba(96,165,250,0.5)" },
  "Audit Sent": { color: "#D4AF37", bg: "rgba(212,175,55,0.14)", border: "rgba(212,175,55,0.55)" },
  "Closed": { color: "#34D399", bg: "rgba(52,211,153,0.14)", border: "rgba(52,211,153,0.5)" },
  "Active Customer": { color: "#A78BFA", bg: "rgba(167,139,250,0.14)", border: "rgba(167,139,250,0.55)" },
};

const STATUS_OPTIONS = ["Target", "Contacted", "Audit Sent", "Closed", "Active Customer"];
const PAGE_SIZE = 100;

const formatCurrency = (n) => {
  const v = Number(n || 0);
  if (!v) return "—";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export default function StrikeList() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ cities: {}, industries: {}, statuses: {} });
  const [lifecycle, setLifecycle] = useState({ active_customers: 0, weekly_leak_recovered: 0, health_breakdown: {} });
  const [loading, setLoading] = useState(true);

  const [city, setCity] = useState("");
  const [industry, setIndustry] = useState("");
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const fetchLeads = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/leader/leads", { params });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setFacets(data.facets || { cities: {}, industries: {}, statuses: {} });
      setLifecycle(data.lifecycle || { active_customers: 0, weekly_leak_recovered: 0, health_breakdown: {} });
    } catch (e) {
      toast.error("Failed to load strike list");
    } finally {
      setLoading(false);
    }
  };

  // Server-side filters kept minimal — we filter client-side on `q` for snappier UX.
  useEffect(() => {
    fetchLeads({});
  }, []);

  const filtered = useMemo(() => {
    let result = leads;
    if (city) result = result.filter((l) => l.city === city);
    if (industry) result = result.filter((l) => l.industry === industry);
    if (status) result = result.filter((l) => l.status === status);
    if (query) {
      const q = query.toLowerCase();
      result = result.filter((l) => {
        return (
          (l.business_name || "").toLowerCase().includes(q) ||
          (l.contact_name || "").toLowerCase().includes(q) ||
          (l.email || "").toLowerCase().includes(q) ||
          (l.phone || "").toLowerCase().includes(q)
        );
      });
    }
    return result;
  }, [leads, city, industry, status, query]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [city, industry, status, query]);

  const clearFilters = () => {
    setCity("");
    setIndustry("");
    setStatus("");
    setQuery("");
  };

  const anyFilter = city || industry || status || query;
  const visibleLeads = filtered.slice(0, visible);

  return (
    <>
      {/* Grid Summary — top-of-page KPI row */}
      <GridSummary lifecycle={lifecycle} total={total} facets={facets} />

      {/* Amanda Cloud Transfer Loop — intake explainer */}
      <AmandaIntake />

    <section className="glass-strong rounded-sm overflow-hidden" data-testid="strike-list-section">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Crosshair size={16} className="text-[#F43F5E]" />
          <div>
            <div className="hud-label">Strike List · Ohio Titan Register</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              Sourced from Seamless.ai · click any target for the Lead Cockpit
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-400 font-mono-aviation" data-testid="strike-count">
          Showing <span className="text-gold">{filtered.length}</span> of{" "}
          <span className="text-white">{total}</span> leads
        </div>
      </div>

      {/* Search + Clear */}
      <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <Search size={14} />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search business, contact, phone, email..."
            data-testid="strike-search-input"
            className="w-full bg-[#060B14] border border-white/10 rounded-sm pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-gold"
          />
        </div>
        {anyFilter && (
          <button
            onClick={clearFilters}
            data-testid="strike-clear-filters"
            className="text-[11px] text-slate-400 hover:text-[#F43F5E] inline-flex items-center gap-1 font-display uppercase tracking-wider transition-colors"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Facet strip: City */}
      <FacetStrip
        icon={<MapPin size={12} />}
        label="City"
        options={Object.entries(facets.cities || {}).sort()}
        value={city}
        setValue={setCity}
        testIdPrefix="strike-filter-city"
        accent="#60A5FA"
      />
      {/* Facet strip: Industry */}
      <FacetStrip
        icon={<Wrench size={12} />}
        label="Industry"
        options={Object.entries(facets.industries || {}).sort()}
        value={industry}
        setValue={setIndustry}
        testIdPrefix="strike-filter-industry"
        accent="#D4AF37"
      />
      {/* Facet strip: Status */}
      <FacetStrip
        icon={<Crosshair size={12} />}
        label="Status"
        options={STATUS_OPTIONS.map((s) => [s, facets.statuses?.[s] || 0])}
        value={status}
        setValue={setStatus}
        testIdPrefix="strike-filter-status"
        accent="#F43F5E"
        colorMap={STATUS_STYLE}
      />

      {/* Column header */}
      <div className="hidden sm:grid grid-cols-12 gap-3 px-6 py-2 text-[9px] uppercase tracking-wider text-slate-500 border-b border-white/5 bg-[#0A1122]/50">
        <div className="col-span-3">Business</div>
        <div className="col-span-2">City</div>
        <div className="col-span-2">Industry</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-1 text-right">Weekly Leak</div>
        <div className="col-span-1 text-center">Cloud</div>
        <div className="col-span-1 text-right">Cockpit</div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500 text-sm">Loading Ohio Titan register...</div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm" data-testid="strike-list-empty">
          No leads match your filters.
        </div>
      ) : (
        <>
          <div className="divide-y divide-white/5" data-testid="strike-rows">
            {visibleLeads.map((lead) => (
              <Row
                key={lead.id}
                lead={lead}
                onLeadUpdated={(updated) =>
                  setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)))
                }
              />
            ))}
          </div>
          {filtered.length > visible && (
            <div className="px-6 py-5 border-t border-white/5 text-center">
              <button
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                data-testid="strike-load-more"
                className="btn-ghost-gold px-6 py-2 rounded-sm text-xs inline-flex items-center gap-2"
              >
                Load {Math.min(PAGE_SIZE, filtered.length - visible)} more
              </button>
            </div>
          )}
        </>
      )}
    </section>
    </>
  );
}

function GridSummary({ lifecycle, total, facets }) {
  const active = lifecycle.active_customers || 0;
  const recovered = lifecycle.weekly_leak_recovered || 0;
  const activeRate = total > 0 ? Math.round((active / total) * 100) : 0;
  const health = lifecycle.health_breakdown || {};

  const currency = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <section className="mb-6" data-testid="grid-summary">
      <div className="hud-label text-[10px] mb-3 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] light-pulse" />
        Grid Summary · Customer Lifecycle
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<Users size={16} className="text-[#A78BFA]" />}
          label="Active Customers"
          value={active}
          accent="#A78BFA"
          sub={total > 0 ? `${activeRate}% of ${total} leads` : "—"}
          testId="kpi-active-customers"
        />
        <KpiCard
          icon={<DollarSign size={16} className="text-gold" />}
          label="Weekly Leak Recovered"
          value={`${currency(recovered)}/wk`}
          accent="#D4AF37"
          sub={active > 0 ? `${currency(Math.round(recovered * 52))}/yr projected` : "Sign your first customer"}
          testId="kpi-weekly-recovered"
        />
        <KpiCard
          icon={<Activity size={16} className="text-[#22c55e]" />}
          label="Healthy Accounts"
          value={health["Active"] || 0}
          accent="#22c55e"
          sub={
            <span className="flex items-center gap-3 mt-0.5">
              <HealthDot color="#22c55e" count={health["Active"] || 0} label="Active" />
              <HealthDot color="#eab308" count={health["Past Due"] || 0} label="Past Due" />
              <HealthDot color="#ef4444" count={health["Churned"] || 0} label="Churned" />
            </span>
          }
          testId="kpi-healthy"
        />
        <KpiCard
          icon={<Crosshair size={16} className="text-[#F43F5E]" />}
          label="Open Pipeline"
          value={(facets.statuses?.["Target"] || 0) + (facets.statuses?.["Contacted"] || 0) + (facets.statuses?.["Audit Sent"] || 0)}
          accent="#F43F5E"
          sub={`${facets.statuses?.["Closed"] || 0} closed · ${facets.statuses?.["Audit Sent"] || 0} audits out`}
          testId="kpi-pipeline"
        />
      </div>
    </section>
  );
}

function KpiCard({ icon, label, value, sub, accent, testId }) {
  return (
    <div
      className="glass-strong rounded-sm p-5 border-l-2"
      style={{ borderLeftColor: accent }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3 text-slate-400">
        {icon}
        <span className="hud-label text-[9px]">{label}</span>
      </div>
      <div className="font-display text-3xl text-white leading-none mb-2" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[11px] text-slate-500 font-mono-aviation">{sub}</div>
    </div>
  );
}

function HealthDot({ color, count, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }} />
      {count} <span className="text-slate-600">{label}</span>
    </span>
  );
}

function FacetStrip({ icon, label, options, value, setValue, testIdPrefix, accent, colorMap }) {
  return (
    <div className="px-6 py-3 border-b border-white/5 flex items-start gap-3">
      <div className="hud-label text-[9px] flex items-center gap-1.5 pt-1.5 shrink-0 w-16">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <FilterPill
          active={!value}
          onClick={() => setValue("")}
          label="All"
          testId={`${testIdPrefix}-all`}
        />
        {options.map(([opt, count]) => {
          const active = value === opt;
          const style = colorMap?.[opt];
          return (
            <FilterPill
              key={opt}
              active={active}
              onClick={() => setValue(active ? "" : opt)}
              label={opt}
              count={count}
              testId={`${testIdPrefix}-${opt.replace(/\s/g, "-").toLowerCase()}`}
              accent={style?.color || accent}
              activeBg={style?.bg}
              activeBorder={style?.border}
            />
          );
        })}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label, count, testId, accent = "#D4AF37", activeBg, activeBorder }) {
  const style = active
    ? {
        color: accent,
        backgroundColor: activeBg || `${accent}22`,
        borderColor: activeBorder || `${accent}80`,
      }
    : {};
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-display uppercase tracking-wider border transition-colors ${
        active
          ? ""
          : "border-white/10 text-slate-400 hover:text-white hover:border-white/25"
      }`}
      style={style}
    >
      <span>{label}</span>
      {typeof count === "number" && (
        <span className="text-[9px] opacity-70 font-mono-aviation">{count}</span>
      )}
    </button>
  );
}

function Row({ lead, onLeadUpdated }) {
  const navigate = useNavigate();
  const initials = (lead.business_name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const statusCfg = STATUS_STYLE[lead.status] || STATUS_STYLE["Target"];
  const isChampion = !!lead.is_champion;
  const isHeavyHitter = isChampion && lead.champion_rank === 1;
  const badgeText = lead.champion_badge || (isChampion ? "Best of Marion" : null);
  const critique = lead.latest_critique;
  const cockpitPath = `/leader/lead/${lead.id}`;

  // Click anywhere on the row body → navigate. Interactive cells (Cloud,
  // Cockpit link) opt out via their own event handlers.
  const openCockpit = (e) => {
    if (e.defaultPrevented) return;
    navigate(cockpitPath);
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={openCockpit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(cockpitPath);
        }
      }}
      className="block relative hover:bg-white/[0.03] transition-colors group cursor-pointer focus:outline-none focus:bg-white/[0.04]"
      data-testid={`strike-row-${lead.id}`}
      style={
        isChampion
          ? {
              backgroundImage:
                "linear-gradient(90deg, rgba(212,175,55,0.055) 0%, rgba(212,175,55,0.015) 40%, transparent 100%)",
            }
          : undefined
      }
    >
      {isChampion && (
        <span
          className="absolute left-0 top-0 bottom-0 w-0.5"
          style={{
            background: isHeavyHitter
              ? "linear-gradient(180deg, #F5C842, #D4AF37, #F5C842)"
              : "#D4AF37",
            boxShadow: isHeavyHitter ? "0 0 12px rgba(245,200,66,0.55)" : undefined,
          }}
          aria-hidden
        />
      )}
      <div className="grid grid-cols-12 gap-3 px-6 py-4 items-center">
        <div className="col-span-12 sm:col-span-3 flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-sm border flex items-center justify-center font-display text-[11px] shrink-0"
            style={{
              borderColor: isChampion ? "rgba(212,175,55,0.75)" : "rgba(212,175,55,0.4)",
              color: isChampion ? "#F5C842" : "#D4AF37",
              boxShadow: isHeavyHitter ? "0 0 14px rgba(245,200,66,0.4)" : undefined,
              backgroundColor: isChampion ? "rgba(212,175,55,0.08)" : "transparent",
            }}
          >
            {initials}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-white font-display text-sm truncate">{lead.business_name}</div>
              {critique && <CritiqueBadge critique={critique} leadId={lead.id} />}
              {badgeText && (
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-display uppercase tracking-widest border ${
                    isHeavyHitter ? "champion-glow" : ""
                  }`}
                  style={{
                    color: isHeavyHitter ? "#060B14" : "#F5C842",
                    backgroundColor: isHeavyHitter ? "#F5C842" : "rgba(212,175,55,0.14)",
                    borderColor: "rgba(212,175,55,0.6)",
                  }}
                  data-testid={`strike-champion-badge-${lead.id}`}
                >
                  {isHeavyHitter && <Trophy size={9} />}
                  {badgeText}
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-500 truncate">
              {lead.contact_name}
              <span className="sm:hidden"> · {lead.city} · {lead.industry}</span>
            </div>
          </div>
        </div>
        <div className="hidden sm:block sm:col-span-2 text-xs text-slate-300 truncate">
          {lead.city}
        </div>
        <div className="hidden sm:block sm:col-span-2 text-xs text-slate-400 truncate">
          {lead.industry}
        </div>
        <div className="col-span-6 sm:col-span-2">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-sm text-[10px] font-display uppercase tracking-wider border"
            style={{
              color: statusCfg.color,
              backgroundColor: statusCfg.bg,
              borderColor: statusCfg.border,
            }}
            data-testid={`strike-status-pill-${lead.id}`}
          >
            {lead.status || "Target"}
          </span>
        </div>
        <div
          className="col-span-4 sm:col-span-1 text-right font-mono-aviation text-sm"
          style={{ color: lead.weekly_leak > 0 ? "#D4AF37" : "#64748B" }}
          data-testid={`strike-leak-${lead.id}`}
        >
          {formatCurrency(lead.weekly_leak)}
        </div>
        <div
          className="col-span-4 sm:col-span-1 flex justify-center"
          data-testid={`strike-cloud-cell-${lead.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <CloudFolderCell lead={lead} onUpdated={onLeadUpdated} />
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-end">
          <Link
            to={cockpitPath}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11px] font-display text-slate-500 group-hover:text-gold transition-colors"
            data-testid={`strike-cockpit-link-${lead.id}`}
          >
            Open <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function CritiqueBadge({ critique, leadId }) {
  const score = Number(critique.overall_score || 0);
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-display uppercase tracking-widest border"
      style={{
        color: "#F5C842",
        backgroundColor: "rgba(212,175,55,0.12)",
        borderColor: "rgba(212,175,55,0.55)",
        boxShadow: "0 0 8px rgba(212,175,55,0.35)",
      }}
      title={`Sales Critique · ${score.toFixed(1)} / 10`}
      data-testid={`strike-critique-badge-${leadId}`}
    >
      <Trophy size={9} /> Critique {score.toFixed(1)}
    </span>
  );
}
